import { Patient, PatientData, ProductData } from '@mmx/shared'
import { extend } from 'lodash'

import type { Workflow } from '../workflow'

/** Mirrors the API's `WorkFile.STATUS`, which tracks each worklist file automation separately. */
export enum WorkFileStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type WorkFileAutomationStatuses = Partial<Record<Workflow.WORKLIST_FILE_ACTION, WorkFileStatus>>

export interface DocumentSignatureInfo {
  /** Total number of detected handwritten signatures across the document's pages */
  count: number
  /** Page numbers (1-indexed) where at least one signature was detected */
  pages: number[]
}

export interface DocumentPageData {
  pages: number[]
  patient: Partial<PatientData>
  confidence: number
  explainConfidence: string
  type: string
  appointmentId?: string
  additionalFields?: Record<string, any>
  signatures?: DocumentSignatureInfo
  product?: ProductData
}

export class DocumentPage {
  /**
   * The pages of the document that were classified
   * @example [1, 2, 3]
   */
  pages: number[]
  patient: Patient
  confidence: number
  explainConfidence: string
  /**
   * Document type, e.g. "Order", "Clinical History", etc.
   */
  type: string
  /**
   * Additional fields extracted from the document
   * Based on the document type, the fields may vary
   */
  additionalFields?: Record<string, any>
  /**
   * Handwritten-signature detection results from Textract for the document's pages.
   * Distinguishes signed vs unsigned forms (e.g. authorization-status vs authorization-form).
   */
  signatures?: DocumentSignatureInfo
  /**
   * The appointment ID is set when the document type is "order"
   * and an appointment record is created
   */
  appointmentId?: string

  constructor(data: DocumentPageData) {
    extend(this, data)

    if (data.patient) {
      this.patient = new Patient(data.patient)
    }
  }
}

export interface WorkItemFileData {
  data: {
    bucket: string
    object: string
  }
  name?: string
  pages?: number
  size?: number
  extension: string
  signedUrl?: string
  signedBlocksUrl?: string
  documents?: DocumentPageData[]
  automationStatues?: WorkFileAutomationStatuses | null
  copiedPages?: Record<number, { bucket: string, object: string }>
}

export class WorkItemFile {
  data: {
    bucket: string
    object: string
    name?: string
    /**
     * Total number of pages in the file
     */
    pages?: number
    /**
     * Size of the file in bytes
     */
    size?: number
  }

  /**
   * File extension
   * @example .pdf
   */
  extension: string
  /**
   * URL to access the file
   */
  signedUrl?: string
  /**
   * URL to access the Textract blocks of the file
   */
  signedBlocksUrl?: string
  /**
   * The documents extracted from the file
   * Each document contains the pages that were classified
   */
  documents?: DocumentPage[]
  /**
   * Progress of each worklist file automation, keyed by action.
   * `identify-type` is owned by whichever classifier ran, so it reports document extraction.
   */
  automationStatues?: WorkFileAutomationStatuses | null
  /**
   * Indicates which pages were copied to the patient record
   */
  copiedPages?: Record<number, { bucket: string, object: string }>

  constructor(
    public fileData: WorkItemFileData,
  ) {
    extend(this, fileData)

    if (fileData.documents) {
      this.documents = fileData.documents.map((d) => new DocumentPage(d))
    }
  }
}
